/**
 * Workbook Documentation Panel Component
 * Affiche la documentation générée du classeur
 */

import React, { useState } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import type { WorkbookDocumentation } from '@excel-ai-studio/shared';
import { callAnalyzeAPI } from '../../services/workbook-brain-api.service';
import { exportDocumentationToSheet } from '../../services/workbook-brain-writer.service';

interface WorkbookDocumentationPanelProps {
  onExport?: (format: 'markdown' | 'json') => void;
}

export const WorkbookDocumentationPanel: React.FC<WorkbookDocumentationPanelProps> = ({
  onExport,
}) => {
  const [documentation, setDocumentation] = useState<WorkbookDocumentation | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeSection, setActiveSection] = useState<string>('summary');
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const generateDocumentation = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await callAnalyzeAPI({
        includeScanning: true,
        includeUnderstanding: true,
        includeDependencies: true,
        includeRiskAnalysis: true,
        includePerformanceAnalysis: true,
        includeDocumentation: true,
      });

      if (!result.documentation) {
        throw new Error('Aucune documentation retournée par le serveur');
      }

      setDocumentation(result.documentation);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de générer la documentation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportToSheet = async () => {
    if (!documentation) return;
    
    try {
      setMessage(null);
      const content = [
        documentation.executiveSummary,
        `Domaine: ${documentation.businessDomain}`,
        `Objectif: ${documentation.workbookPurpose}`,
        `Complexité: ${documentation.technicalAppendix.estimatedComplexity}`,
      ];
      await exportDocumentationToSheet('Documentation du Classeur', content);
      setMessage({ type: 'success', text: '✅ Documentation exportée avec succès' });
      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Erreur lors de l\'export';
      setMessage({ type: 'error', text: `❌ ${errorMsg}` });
      setTimeout(() => setMessage(null), 5000);
    }
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <LoadingSpinner label="Génération de la documentation..." />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* En-tête */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0 }}>📝 Documentation</h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={handleExportToSheet}
            style={{ padding: '8px 12px', backgroundColor: '#9c27b0', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
          >
            📊 Exporter
          </button>
          <button
            onClick={() => onExport?.('markdown')}
            style={{ padding: '8px 12px', backgroundColor: '#4CAF50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
          >
            📄 Markdown
          </button>
          <button
            onClick={() => onExport?.('json')}
            style={{ padding: '8px 12px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' }}
          >
            📋 JSON
          </button>
          <button
            onClick={generateDocumentation}
            style={{ padding: '8px 16px', backgroundColor: '#2196F3', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' }}
          >
            Générer
          </button>
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px', backgroundColor: '#ffebee', color: '#c62828', borderRadius: '4px', fontSize: '13px' }}>
          ❌ {error}
        </div>
      )}

      {message && (
        <div style={{ padding: '12px', backgroundColor: message.type === 'success' ? '#e8f5e9' : '#ffebee', color: message.type === 'success' ? '#2e7d32' : '#c62828', borderRadius: '4px', fontSize: '13px' }}>
          {message.text}
        </div>
      )}

      {documentation && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {/* Navigation des sections */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', borderBottom: '1px solid #ddd', paddingBottom: '8px' }}>
            {['summary', 'sheets', 'dataflow', 'risks', 'recommendations'].map((section) => (
              <button
                key={section}
                onClick={() => setActiveSection(section)}
                style={{
                  padding: '6px 12px',
                  backgroundColor: activeSection === section ? '#2196F3' : '#f0f0f0',
                  color: activeSection === section ? 'white' : '#666',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: activeSection === section ? 'bold' : 'normal',
                }}
              >
                {section === 'summary' && '📊 Résumé'}
                {section === 'sheets' && '📄 Feuilles'}
                {section === 'dataflow' && '➡️ Flux de données'}
                {section === 'risks' && '⚠️ Risques'}
                {section === 'recommendations' && '💡 Recommandations'}
              </button>
            ))}
          </div>

          {/* Section Résumé */}
          {activeSection === 'summary' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '13px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Résumé exécutif</div>
                <div style={{ color: '#666', lineHeight: '1.6' }}>{documentation.executiveSummary}</div>
              </div>
              <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '13px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Objectif</div>
                <div style={{ color: '#666' }}>{documentation.workbookPurpose}</div>
              </div>
              <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '13px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Domaine métier</div>
                <div style={{ color: '#666' }}>{documentation.businessDomain}</div>
              </div>
              <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '13px' }}>
                <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Détails techniques</div>
                <div style={{ color: '#666' }}>
                  <div>Formules : {documentation.technicalAppendix.totalFormulas}</div>
                  <div>Tableaux : {documentation.technicalAppendix.totalTables}</div>
                  <div>Plages nommées : {documentation.technicalAppendix.totalNamedRanges}</div>
                  <div>Complexité : {documentation.technicalAppendix.estimatedComplexity}</div>
                </div>
              </div>
            </div>
          )}

          {/* Section Feuilles */}
          {activeSection === 'sheets' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {documentation.sheetDocs.map((sheet, idx) => (
                <div key={idx} style={{ padding: '12px', backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', fontSize: '12px' }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#1976d2' }}>
                    {sheet.sheetName}
                  </div>
                  <div style={{ color: '#666', marginBottom: '4px' }}>
                    <strong>Rôle :</strong> {sheet.role}
                  </div>
                  {sheet.keyMetrics.length > 0 && (
                    <div style={{ color: '#666', marginBottom: '4px' }}>
                      <strong>Métriques :</strong> {sheet.keyMetrics.join(', ')}
                    </div>
                  )}
                  {sheet.notes.length > 0 && (
                    <div style={{ color: '#666' }}>
                      <strong>Notes :</strong>
                      <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                        {sheet.notes.map((note, i) => (
                          <li key={i}>{note}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Section Flux de données */}
          {activeSection === 'dataflow' && (
            <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', fontSize: '13px' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>Résumé du flux de données</div>
              <div style={{ color: '#666', lineHeight: '1.6' }}>{documentation.dataFlowSummary}</div>
            </div>
          )}

          {/* Section Risques */}
          {activeSection === 'risks' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {documentation.risks.map((risk, idx) => (
                <div key={idx} style={{ padding: '12px', backgroundColor: risk.includes('✅') ? '#e8f5e9' : '#fff3e0', borderRadius: '4px', fontSize: '13px', color: risk.includes('✅') ? '#2e7d32' : '#e65100' }}>
                  {risk}
                </div>
              ))}
            </div>
          )}

          {/* Section Recommandations */}
          {activeSection === 'recommendations' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {documentation.recommendations.map((rec, idx) => (
                <div key={idx} style={{ padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', fontSize: '13px', color: '#1565c0' }}>
                  ✓ {rec}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
